import React, { useEffect, useMemo, useState } from 'react';
import { Modal, FormLayout, TextField, InlineStack, Badge, Text, BlockStack, Select, Button } from '@shopify/polaris';
import { configApi } from '../../../services/api/configApi';

const DEFAULT_SANDBOX_BASE_URL = 'https://sandbox-quickbooks.api.intuit.com';
const DEFAULT_PRODUCTION_BASE_URL = 'https://quickbooks.api.intuit.com';
const DEFAULT_TOKEN_URL = 'https://oauth.platform.intuit.com/oauth2/v1/tokens/bearer';
const DEFAULT_REDIRECT_URI = process.env.REACT_APP_QUICKBOOKS_REDIRECT_URI || 'https://api-staging.cartnxs.com/oauth/quickbooks/callback';

function defaultBaseUrl(environment) {
  return environment === 'production' ? DEFAULT_PRODUCTION_BASE_URL : DEFAULT_SANDBOX_BASE_URL;
}

function normalizeBaseUrl(environment, baseUrl) {
  if (!baseUrl) return defaultBaseUrl(environment);
  if (environment === 'sandbox' && baseUrl === DEFAULT_PRODUCTION_BASE_URL) return DEFAULT_SANDBOX_BASE_URL;
  if (environment === 'production' && baseUrl === DEFAULT_SANDBOX_BASE_URL) return DEFAULT_PRODUCTION_BASE_URL;
  return baseUrl;
}

export function QuickBooksCredentialsModal({ open, onClose, onSave, config = {} }) {
  const [formData, setFormData] = useState({
    environment: 'sandbox',
    realmId: '',
    clientId: '',
    clientSecret: '',
    refreshToken: '',
    baseUrl: DEFAULT_SANDBOX_BASE_URL,
    tokenUrl: DEFAULT_TOKEN_URL,
    minorVersion: '75',
    redirectUri: DEFAULT_REDIRECT_URI
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [status, setStatus] = useState({ ok: null, message: '' });

  useEffect(() => {
    if (!open) return;
    const environment = config.environment || 'sandbox';
    setFormData({
      environment,
      realmId: config.realmId || '',
      clientId: config.clientId || '',
      clientSecret: config.clientSecret || '',
      refreshToken: config.refreshToken || '',
      baseUrl: normalizeBaseUrl(environment, config.baseUrl),
      tokenUrl: config.tokenUrl || DEFAULT_TOKEN_URL,
      minorVersion: config.minorVersion || '75',
      redirectUri: config.redirectUri || DEFAULT_REDIRECT_URI
    });
    setShowAdvanced(false);
    setStatus({ ok: null, message: '' });
  }, [open, config]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('quickbooks_connected') === '1') {
      window.history.replaceState({}, '', window.location.pathname);
      if (onSave) onSave();
    } else if (params.get('quickbooks_error')) {
      window.history.replaceState({}, '', window.location.pathname);
      setStatus({ ok: false, message: `Connection failed: ${params.get('quickbooks_error')}` });
    }
  }, [onSave]);

  const canSave = useMemo(() => (
    !!formData.realmId.trim() &&
    !!formData.clientId.trim() &&
    !!formData.clientSecret.trim()
  ), [formData]);
  const canConnect = useMemo(() => (
    !!formData.clientId.trim() &&
    !!formData.clientSecret.trim() &&
    !!formData.redirectUri.trim()
  ), [formData]);

  const payload = () => ({
    environment: formData.environment,
    realmId: formData.realmId.trim(),
    clientId: formData.clientId.trim(),
    clientSecret: formData.clientSecret === '****' ? undefined : formData.clientSecret,
    refreshToken: formData.refreshToken === '****' ? undefined : formData.refreshToken,
    baseUrl: formData.baseUrl.trim().replace(/\/$/, ''),
    tokenUrl: formData.tokenUrl.trim(),
    minorVersion: formData.minorVersion.trim() || '75',
    redirectUri: formData.redirectUri.trim()
  });

  const handleSave = async () => {
    setSaving(true);
    setStatus({ ok: null, message: '' });
    try {
      await configApi.saveQuickBooksCredentials(payload());
      setStatus({ ok: true, message: 'QuickBooks settings saved' });
      if (onSave) await onSave();
      onClose();
    } catch (error) {
      setStatus({ ok: false, message: error.message || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    setStatus({ ok: null, message: '' });
    try {
      const result = await configApi.startQuickBooksOAuth(payload());
      if (!result.success || !result.authUrl) {
        throw new Error(result.error || 'Failed to start OAuth');
      }
      window.location.href = result.authUrl;
    } catch (error) {
      setStatus({ ok: false, message: error.message || 'Failed to start QuickBooks OAuth' });
      setConnecting(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setStatus({ ok: null, message: 'Testing connection...' });
    try {
      const result = await configApi.validateCredentials('quickbooks', payload());
      if (result.success && result.valid) {
        setStatus({ ok: true, message: result.message || 'Connection successful' });
      } else {
        const detailMessage = typeof result.details === 'string'
          ? result.details
          : (result.details?.Fault?.Error?.[0]?.Message || result.details?.error_description || result.details?.error || result.details?.message);
        setStatus({ ok: false, message: detailMessage ? `${result.error || 'Connection failed'}: ${detailMessage}` : (result.error || 'Connection failed') });
      }
    } catch (error) {
      setStatus({ ok: false, message: error.message || 'Connection failed' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="QuickBooks Online Connection"
      primaryAction={{
        content: 'Save',
        onAction: handleSave,
        disabled: !canSave,
        loading: saving
      }}
      secondaryActions={[
        { content: 'Connect with QuickBooks', onAction: handleConnect, disabled: !canConnect || connecting, loading: connecting },
        { content: 'Test connection', onAction: handleTest, disabled: !canSave || testing || !formData.refreshToken.trim(), loading: testing },
        { content: 'Cancel', onAction: onClose }
      ]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          <FormLayout>
            <Select
              label="Environment"
              options={[
                { label: 'Sandbox', value: 'sandbox' },
                { label: 'Production', value: 'production' }
              ]}
              value={formData.environment}
              onChange={(value) => setFormData(prev => ({
                ...prev,
                environment: value,
                baseUrl: normalizeBaseUrl(value, prev.baseUrl)
              }))}
            />
            <TextField label="Client ID" value={formData.clientId} onChange={(value) => setFormData(prev => ({ ...prev, clientId: value }))} autoComplete="off" />
            <TextField
              label="Client Secret"
              value={formData.clientSecret}
              onChange={(value) => setFormData(prev => ({ ...prev, clientSecret: value }))}
              type="password"
              autoComplete="off"
              helpText={config.clientSecret === '****' ? 'Stored; leave as **** to keep unchanged' : 'From Intuit Developer app keys and credentials'}
            />
            <TextField label="Redirect URI" value={formData.redirectUri} onChange={(value) => setFormData(prev => ({ ...prev, redirectUri: value }))} autoComplete="off" />
          </FormLayout>

          <Button onClick={() => setShowAdvanced((value) => !value)}>
            {showAdvanced ? 'Hide advanced settings' : 'Show advanced settings'}
          </Button>

          {showAdvanced && (
            <FormLayout>
              <TextField label="Realm ID" value={formData.realmId} onChange={(value) => setFormData(prev => ({ ...prev, realmId: value }))} autoComplete="off" />
              <TextField
                label="Refresh Token"
                value={formData.refreshToken}
                onChange={(value) => setFormData(prev => ({ ...prev, refreshToken: value }))}
                type="password"
                autoComplete="off"
                helpText={config.refreshToken === '****' ? 'Stored; leave as **** to keep unchanged' : 'Created by the QuickBooks OAuth connect flow'}
              />
              <TextField label="API URL" value={formData.baseUrl} onChange={(value) => setFormData(prev => ({ ...prev, baseUrl: value }))} autoComplete="off" />
              <TextField label="Token URL" value={formData.tokenUrl} onChange={(value) => setFormData(prev => ({ ...prev, tokenUrl: value }))} autoComplete="off" />
              <TextField label="Minor Version" value={formData.minorVersion} onChange={(value) => setFormData(prev => ({ ...prev, minorVersion: value }))} autoComplete="off" />
            </FormLayout>
          )}

          {status.message && (
            <InlineStack gap="200">
              <Badge tone={status.ok ? 'success' : status.ok === false ? 'critical' : 'info'}>
                {status.ok ? 'Ready' : status.ok === false ? 'Failed' : 'Working'}
              </Badge>
              <Text as="span">{status.message}</Text>
            </InlineStack>
          )}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}

export default QuickBooksCredentialsModal;
